// frontend/src/pages/Register.js
import { useState } from "react";
import { BACKEND_URL } from "../config";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

const isValidEmail = (value = "") => /\S+@\S+\.\S+/.test(value.trim());
const isValidPhone = (value = "") => /^\d{8,}$/.test(value.trim());

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [name, setName] = useState("");
  const [consultancyName, setConsultancyName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({ email: "", phone: "" });
  const [role, setRole] = useState("student"); // default

  const register = async () => {
    setError("");

    const trimmedName = name.trim();
    const trimmedPassword = password.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();

    if (!trimmedName || !trimmedPassword) {
      setError("Name and password are required");
      return;
    }

    if (!trimmedEmail && !trimmedPhone) {
      setError("Please provide at least an email or mobile number.");
      return;
    }

    const nextFieldErrors = { email: "", phone: "" };
    let hasFieldError = false;

    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      nextFieldErrors.email = "Enter a valid email address";
      hasFieldError = true;
    }

    if (trimmedPhone && !isValidPhone(trimmedPhone)) {
      nextFieldErrors.phone = "Enter a valid mobile number";
      hasFieldError = true;
    }

    if (hasFieldError) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    setFieldErrors({ email: "", phone: "" });

    const payload = {
      name: trimmedName,
      email: trimmedEmail || undefined,
      phone: trimmedPhone || undefined,
      password: trimmedPassword,
      role,
      consultancyName: role === "consultancy" ? consultancyName.trim() : undefined,
    };

    const response = await fetch(`${BACKEND_URL}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    console.log("Register response:", data);

    if (data.error) {
      setError(data.error);
      return;
    }

    if (data?.token && data?.user) {
      login(data.user, data.token);
      const nextRole = data.user.role === "consultancy" ? "owner" : data.user.role;
      if (nextRole === "owner") {
        navigate("/owner/dashboard", { replace: true });
      } else if (nextRole === "admin") {
        navigate("/admin", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
      return;
    }

    navigate("/login");
  };

  return (
    <main className="min-h-screen bg-slate-50 text-slate-950">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 py-10 sm:px-6 lg:grid-cols-[1fr_430px] lg:py-14">
          <div className="flex flex-col justify-center">
            <p className="text-xs font-semibold uppercase tracking-[3px] text-cyan-700">
              Create account
            </p>
            <h1 className="mt-3 max-w-3xl text-3xl font-semibold leading-tight text-slate-950 md:text-5xl">
              Start your study abroad journey with AbroadJacks.
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600">
              Students can shortlist consultancies and contact them securely. Consultancy owners can create an account to manage their profile, offers, and student enquiries.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[2px] text-slate-500">Network</p>
                <p className="mt-2 text-2xl font-semibold">1000+</p>
                <p className="mt-1 text-xs text-slate-500">Consultancy listings</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[2px] text-slate-500">Access</p>
                <p className="mt-2 text-2xl font-semibold">Free</p>
                <p className="mt-1 text-xs text-slate-500">Student signup</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[2px] text-slate-500">Tools</p>
                <p className="mt-2 text-2xl font-semibold">1</p>
                <p className="mt-1 text-xs text-slate-500">Dashboard after signup</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[2px] text-slate-500">Step 1 of 2</p>
              <h2 className="text-2xl font-semibold text-slate-950">Create your account</h2>
              <p className="text-sm text-slate-500">Use email, mobile number, or both.</p>
            </div>

            {error && (
              <div className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <div className="mt-6 grid grid-cols-2 gap-2 rounded-lg border border-slate-200 bg-slate-100 p-1">
              <button
                type="button"
                onClick={() => setRole("student")}
                className={`rounded-md py-2 text-sm font-semibold transition ${
                  role === "student"
                    ? "bg-white text-slate-950 shadow-sm"
                    : "text-slate-600 hover:text-slate-950"
                }`}
              >
                Student
              </button>
              <button
                type="button"
                onClick={() => setRole("consultancy")}
                className={`rounded-md py-2 text-sm font-semibold transition ${
                  role === "consultancy"
                    ? "bg-white text-slate-950 shadow-sm"
                    : "text-slate-600 hover:text-slate-950"
                }`}
              >
                Consultancy
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Full name</label>
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-950 placeholder-slate-400 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                  placeholder="Enter your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              {role === "consultancy" && (
                <div>
                  <label className="text-sm font-medium text-slate-700">Consultancy name</label>
                  <input
                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-950 placeholder-slate-400 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                    placeholder="Enter consultancy name"
                    value={consultancyName}
                    onChange={(e) => setConsultancyName(e.target.value)}
                  />
                </div>
              )}

              <div>
                <label className="flex items-center justify-between text-sm font-medium text-slate-700">
                  <span>Email</span>
                  <small className="text-xs font-normal text-slate-500">
                    Email or mobile required
                  </small>
                </label>
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-950 placeholder-slate-400 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {fieldErrors.email && (
                  <p className="mt-1 text-xs text-rose-600">{fieldErrors.email}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Mobile number</label>
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-950 placeholder-slate-400 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                  placeholder="+91 98765 43210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
                {fieldErrors.phone && (
                  <p className="mt-1 text-xs text-rose-600">{fieldErrors.phone}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Password</label>
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-950 placeholder-slate-400 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                  type="password"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <button
              className="mt-6 w-full rounded-lg bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
              onClick={register}
            >
              Create account
            </button>

            <p className="mt-5 text-center text-sm text-slate-600">
              Already have an account?{" "}
              <button
                type="button"
                className="font-semibold text-slate-950 underline-offset-2 hover:underline"
                onClick={() => navigate("/login")}
              >
                Login
              </button>
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
